import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isTokenExpired, clearSession } from "../../utils/Auth/auth";
import { postRequest } from "../../services/api";
import logo from "../../assets/acadebility-logo.png";
import "./Header.css";

const Header = () => {
  const [username, setUsername] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // useEffect(() => {
  //   const user = JSON.parse(localStorage.getItem("user") || "null");
  //   if (user) setUsername(user.firstname);
  // }, []);
//   useEffect(() => {
//   const user = JSON.parse(localStorage.getItem("user") || "null");
//   setUsername(user?.firstname || null);

//   const sync = () => {
//     const user = JSON.parse(localStorage.getItem("user") || "null");
//     setUsername(user?.firstname || null);
//   };

//   window.addEventListener("storage", sync);

//   return () => window.removeEventListener("storage", sync);
// }, []);
/* ── Read user from localStorage and check token validity ── */
  const syncUser = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      if (token) clearSession(); // clean up expired token + fire storage event
      setUsername(null);
    } else {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      setUsername(user?.firstname ?? null);
    }
  }, []);
 
  useEffect(() => {
    syncUser();
    // Re-sync when another tab logs in or out
    window.addEventListener("storage", syncUser);
    // Poll every 30 s so this tab notices its own token expiry
    const id = setInterval(syncUser, 30_000);
    return () => {
      window.removeEventListener("storage", syncUser);
      clearInterval(id);
    };
  }, [syncUser]);

  // const handleLogout = () => {
  //   localStorage.removeItem("token");
  //   localStorage.removeItem("user");
  //   setUsername(null);
  //   navigate("/");
  // };
  const handleLogout = async () => {
    try {
      // This endpoint takes user_id directly in the body and doesn't
      // require auth, so pull the id straight from the locally stored
      // user object rather than relying on a token.
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      if (storedUser?.id) {
        await postRequest("/auth/logout", { user_id: storedUser.id });
      }
    } catch (err) {
      // Even if the server call fails (e.g. network issue, expired token),
      // still log the user out locally — don't leave them stuck signed in
      // on the client just because the backend call didn't succeed.
      console.error("Logout API call failed:", err);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUsername(null);
      navigate("/");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(searchTerm)}&page=1`);
    }
  };

  return (
    <header className="acad-header">
      <div className="acad-header-inner">

        {/* ── Logo ── */}
        <Link to="/" className="acad-logo" aria-label="Acadebility Home">
          {/* SVG Logo mark */}
          {/* <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" className="acad-logo-icon">
            <rect width="34" height="34" rx="9" fill="#0d9e8a"/>
            <path d="M17 6L27 11.5V22.5L17 28L7 22.5V11.5L17 6Z" fill="rgba(255,255,255,0.15)" stroke="white" strokeWidth="1.5"/>
            <path d="M11 15L17 12L23 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 12V22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <path d="M11 15V21L17 24L23 21V15" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg> */}
          <img
            src={logo}
            alt="Acadebility Logo"
            width={34}
            height={34}
            className="acad-logo-icon"
          />
          <span className="acad-logo-text">acade<span className="acad-logo-accent">bility</span></span>
        </Link>

        {/* ── Search ── */}
        <form className="acad-search-form" onSubmit={handleSearch} role="search">
          <svg className="acad-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className="acad-search-input"
            placeholder="Search tests, MCQs, blogs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search"
          />
          {searchTerm && (
            <button type="button" className="acad-search-clear" onClick={() => setSearchTerm("")} aria-label="Clear search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </form>

        {/* ── Desktop Nav ── */}
        <nav className="acad-nav" aria-label="Main navigation">
          <Link to="/online-test-series" className="acad-nav-link">Test Series</Link>
          <Link to="/blogs/all-blogs/mcqs" className="acad-nav-link">MCQs</Link>
          <Link to="/blogs/all-blogs/posts" className="acad-nav-link">Blogs</Link>

          {!username ? (
            <>
              <Link to="/register" className="acad-nav-btn acad-nav-btn-ghost">Sign Up</Link>
              <Link to="/login" className="acad-nav-btn acad-nav-btn-primary">Sign In</Link>
            </>
          ) : (
            <div className="acad-dropdown">
              <button type="button" className="acad-dropdown-trigger">
                <span className="acad-avatar">{username.charAt(0).toUpperCase()}</span>
                Hi, {username}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="acad-dropdown-menu">
                <Link to="/profile"          className="acad-dropdown-item">Profile</Link>
                <Link to="/dashboard"        className="acad-dropdown-item">My Dashboard</Link>
                <Link to="/tests"            className="acad-dropdown-item">My Tests</Link>
                <Link to="/change-password"  className="acad-dropdown-item">Change Password</Link>
                <button type="button" className="acad-dropdown-item acad-dropdown-logout" onClick={handleLogout}>Logout</button>
              </div>
            </div>
          )}
        </nav>

        {/* ── Mobile hamburger ── */}
        <button
          type="button"
          className={`acad-hamburger ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span/><span/><span/>
        </button>
      </div>

      {/* ── Mobile menu ── */}
      <div className={`acad-mobile-menu ${menuOpen ? "open" : ""}`}>
        <form className="acad-mobile-search" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="acad-search-input"
          />
          <button type="submit" className="acad-mobile-search-btn">Search</button>
        </form>
        <Link to="/online-test-series" className="acad-mobile-link" onClick={() => setMenuOpen(false)}>Test Series</Link>
        <Link to="/blogs/all-blogs/mcqs" className="acad-mobile-link" onClick={() => setMenuOpen(false)}>MCQs</Link>
        <Link to="/blogs/all-blogs/posts" className="acad-mobile-link" onClick={() => setMenuOpen(false)}>Blogs</Link>
        {!username ? (
          <div className="acad-mobile-auth">
            <Link to="/register" className="acad-nav-btn acad-nav-btn-ghost" onClick={() => setMenuOpen(false)}>Sign Up</Link>
            <Link to="/login"    className="acad-nav-btn acad-nav-btn-primary" onClick={() => setMenuOpen(false)}>Sign In</Link>
          </div>
        ) : (
          <>
            <Link to="/profile"   className="acad-mobile-link" onClick={() => setMenuOpen(false)}>Profile</Link>
            <Link to="/dashboard" className="acad-mobile-link" onClick={() => setMenuOpen(false)}>Dashboard</Link>
            <button type="button" className="acad-mobile-link acad-mobile-logout" onClick={() => { handleLogout(); setMenuOpen(false); }}>Logout</button>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
